/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IGetCustFreightTermCodeResponseV2 } from './DataServiceEntities.g';
import { getCustFreightTermCodeV2Async } from './DataActionExtension.g';
import { generateCacheKey } from '@msdyn365-commerce-modules/retail-actions';

/**
 * GetCustomerFreightTermCode Input Action
 */
export class GetCustomerFreightTermCodeInput implements Msdyn365.IActionInput {
    public apiSettings: Msdyn365.ICommerceApiSettings;
    public customerAccountNumber: string;

    constructor(apiSettings: Msdyn365.ICommerceApiSettings, customerAccountNumber: string) {
        this.customerAccountNumber = customerAccountNumber;
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () =>
        generateCacheKey(
            this.apiSettings,
            { key: 'type', value: 'CustomerFreightTermCode' },
            { key: 'customerAccountNumber', value: this.customerAccountNumber || 'anonymous' }
        );

    public getCacheObjectType = () => 'CustomerFreightTermCode';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

export interface IGetCustomerFreightTermCodeData {
    freightTermCodeResp: IGetCustFreightTermCodeResponseV2;
}

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    const apiSettings = args.requestContext.apiSettings;
    const customerAccountNumber = args.requestContext.user?.customerAccountNumber ?? '';
    return new GetCustomerFreightTermCodeInput(apiSettings, customerAccountNumber);
};

async function action(input: GetCustomerFreightTermCodeInput, ctx: Msdyn365.IActionContext): Promise<IGetCustomerFreightTermCodeData> {
    const resp = await getCustFreightTermCodeV2Async({
        callerContext: ctx
    });
    return { freightTermCodeResp: resp };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetCustomerFreightTermCodeData>>action,
    id: 'GetCustomerFreightTermCode',
    input: createInput
});
